import {
  clampProductSize,
  DEFAULT_PRODUCT_SIZE,
  resolveProductVisual,
} from './productVisualGeometry';
import type { ProductVisualStyle } from './liveFrameAnimation';
import type { ProjectDocument } from '../types/project';
import type { ProductTypeConfig } from '../types/nodeConfig';

// ------------------------------------------------------------
// Product visual catalog helpers (mirrors simulation engine sizing)
// ------------------------------------------------------------

export function resolveProductTypeSizePx(
  productType: ProductTypeConfig,
  pixelsPerMeter: number,
): number {
  const explicit = productType.size ?? (productType as { size_px?: number }).size_px;
  if (explicit != null && explicit > 0) {
    return clampProductSize(explicit);
  }

  if (
    productType.use_real_dimensions &&
    (productType.length_cm ?? 0) > 0 &&
    (productType.width_cm ?? 0) > 0
  ) {
    const maxDimM =
      Math.max(productType.length_cm ?? 0, productType.width_cm ?? 0) / 100;
    const px = maxDimM * Math.max(pixelsPerMeter, 1);
    if (px > 0) {
      return clampProductSize(px);
    }
  }

  const weightKg = productType.weight_kg ?? 1;
  if (weightKg > 0 && Math.abs(weightKg - 1) > 1e-6) {
    return clampProductSize(DEFAULT_PRODUCT_SIZE * Math.sqrt(weightKg));
  }

  return DEFAULT_PRODUCT_SIZE;
}

function styleKey(sourceInputId: string, productTypeId: string): string {
  return `${sourceInputId}:${productTypeId}`;
}

export function collectVisualCatalogFromDocument(
  document: ProjectDocument,
): ProductVisualStyle[] {
  const pixelsPerMeter = document.settings?.pixels_per_meter ?? 40;
  const styles: ProductVisualStyle[] = [];

  for (const node of document.nodes) {
    if (node.type !== 'input') continue;
    const productTypes =
      (node.product_types as ProductTypeConfig[] | undefined) ?? [];
    for (const productType of productTypes) {
      if (productType.enabled === false) continue;
      const resolved = resolveProductVisual({
        color: productType.color,
        shape: productType.shape,
        size: resolveProductTypeSizePx(productType, pixelsPerMeter),
      });
      const key = styleKey(node.id, productType.id);
      styles.push({
        style_id: key,
        source_input_id: node.id,
        product_type_id: productType.id,
        color: resolved.color,
        shape: resolved.shape,
        size_px: resolved.size_px,
      });
    }
  }

  return styles;
}
